import type { LinkTag, MetaTag } from "next-seo";
import { DefaultSeo as Default } from "next-seo";
import type { FunctionComponent } from "react";

import { config } from "~/data";
import { useCanonicalUrl } from "~/lib/hooks";
import { getAssetPrefix } from "~/lib/utils";

import { DefaultTitleTemplate, Title } from "./consts";
import DefaultImage from "./default-image.png";
import { useKeywordMetaTags } from "./hooks";

export const DefaultSeo: FunctionComponent = () => {
  const description =
    "Your source for credible news and authoritative insights from Hong Kong, China and the world.";
  const { canonicalUrl } = useCanonicalUrl();
  const keywordMetaTags = useKeywordMetaTags();

  return (
    <Default
      additionalLinkTags={[appManifestLinkTag, ...appIconLinkTags]}
      additionalMetaTags={[...appLinksMetaTags, ...keywordMetaTags]}
      canonical={canonicalUrl}
      defaultTitle={Title}
      description={description}
      facebook={{ appId: config.social.facebook.appId }}
      openGraph={{
        description,
        images: [{ url: DefaultImage.src }],
        site_name: Title,
        title: Title,
        type: "website",
        url: canonicalUrl,
      }}
      robotsProps={{ maxImagePreview: "large" }}
      titleTemplate={DefaultTitleTemplate}
      twitter={{
        cardType: "summary_large_image",
        site: `@${config.social.twitter.accountName}`,
      }}
    />
  );
};
DefaultSeo.displayName = "DefaultSeo";

const appLinksMetaTags: MetaTag[] = [
  {
    content: config.app.ios.storeId,
    property: "al:ios:app_store_id",
  },
  {
    content: config.app.ios.name,
    property: "al:ios:app_name",
  },
  {
    content: config.app.android.package,
    property: "al:android:package",
  },
  {
    content: config.app.android.name,
    property: "al:android:app_name",
  },
  {
    content: `app-id=${config.app.ios.storeId}`,
    name: "apple-itunes-app",
  },
];

const appManifestLinkTag: LinkTag = {
  href: "/static/manifest.json",
  rel: "manifest",
};

const appIconLinkTags: LinkTag[] = [
  {
    href: `${getAssetPrefix()}/favicon.ico`,
    rel: "shortcut icon",
  },
  {
    href: `${getAssetPrefix()}/icons/scmp-icon-192x192.png`,
    rel: "icon",
    sizes: "192x192",
  },
  {
    href: `${getAssetPrefix()}/icons/scmp-icon-256x256.png`,
    rel: "icon",
    sizes: "256x256",
  },
  {
    href: `${getAssetPrefix()}/icons/scmp-icon-192x192.png`,
    rel: "apple-touch-icon",
  },
  {
    href: `${getAssetPrefix()}/icons/scmp-icon-192x192.png`,
    rel: "apple-touch-icon-precomposed",
  },
];

export const ViewportTag = {
  content: "width=device-width, maximum-scale=1, user-scalable=no, initial-scale=1",
  name: "viewport",
};
